import React, { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { DateTime } from 'luxon';
import { DateTimeRange, TaxReportRequest } from '../../../clients/quipli-service/report/report';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { useSearchParams } from 'react-router-dom';
import './tax-report.scss';
import { CashBasisTaxReport } from './cash-basis-tax-report';
import FileSaver from 'file-saver';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import _ from 'lodash';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { toast } from '../../../util/toasts';
import { SpinnerModal } from '../../../components/modal/spinner-modal';

function extractPaymentReportSearchParams(params: URLSearchParams): TaxReportRequest {
  const from = params.get('from') ? DateTime.fromISO(params.get('from')!) : undefined;
  const to = params.get('to') ? DateTime.fromISO(params.get('to')!) : undefined;
  return { from, to };
}

function extractBasisSearchParam(params: URLSearchParams): 'cash' | 'accrual' | undefined {
  let basis = params.get('basis') || undefined;
  if (basis !== 'cash' && basis !== 'accrual') {
    basis = undefined;
  }
  return basis;
}

export const TaxReport = () => {
  const { exportAccrualBasisTaxReport, exportCashBasisTaxReport, getAvailableTaxReportRange } = useReportApiV2();
  const { getMultipleConfigurations } = useAPI();
  const [currency, setCurrency] = useState<string>();
  const [timezone, setTimezone] = useState<string>();
  const [isExporting, setIsExporting] = useState(false);

  // List searchRequest hooks
  const [params] = useSearchParams();
  const { request: reportRequest, setRequest: setReportRequest } = useRequestQueryParams<TaxReportRequest>(
    extractPaymentReportSearchParams(params),
    () => {},
  );

  const [basis /*setBasis*/] = useState<'cash' | 'accrual'>(extractBasisSearchParam(params) || 'cash');
  const [availableTaxReportRange, setAvailableTaxReportRange] = useState<DateTimeRange>();

  const loadTimezone = async () => {
    const confs = await getMultipleConfigurations(['CONF_TIMEZONE', 'CONF_CURRENCY']);
    const timezone = confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
    const currency = confs.find((c) => c.variable === 'CONF_CURRENCY')?.value;
    setCurrency(currency || 'USD');
    if (timezone) {
      setTimezone(timezone);
      if (!reportRequest.from && !reportRequest.to) {
        setReportRequest((req) => {
          return {
            ...req,
            from: DateTime.local({ zone: timezone }).startOf('month'),
            to: DateTime.local({ zone: timezone }).endOf('month'),
          };
        });
      }
    }
  };

  useEffect(() => {
    loadTimezone();
  }, []);

  useEffect(() => {
    getAvailableTaxReportRange(basis).then(setAvailableTaxReportRange);
  }, [basis]);

  const exportReport = async () => {
    if (reportRequest) {
      setIsExporting(true);
      try {
        let file;
        if (basis === 'cash') {
          file = await exportCashBasisTaxReport({ from: reportRequest.from, to: reportRequest.to });
        } else {
          file = await exportAccrualBasisTaxReport({ from: reportRequest.from, to: reportRequest.to });
        }
        const exportFileName = `${_.capitalize(basis)}_Basis_Tax_Report_${reportRequest
          .from!.toFormat('D')
          .replaceAll('/', '-')}`;
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const fileExtension = '.xlsx';

        const data = new Blob([file], { type: fileType });
        FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
      } catch (e) {
        console.error(e);
        toast('Failed to export report', 'error');
      } finally {
        setIsExporting(false);
      }
    }
  };

  const reportingMonths: DateTimeRange[] = [];
  if (availableTaxReportRange) {
    let currentStart = availableTaxReportRange.from.startOf('month');
    while (currentStart.diffNow().milliseconds < 0) {
      reportingMonths.push({ from: currentStart, to: currentStart.endOf('month') });
      currentStart = currentStart.plus({ months: 1 }).setZone(timezone, { keepLocalTime: true });
    }
  }
  reportingMonths.reverse();
  let selectedMonth = -1;
  if (reportRequest.from && reportRequest.to) {
    selectedMonth = reportingMonths.findIndex(
      (m) => m.from.diff(reportRequest.from!).milliseconds === 0 && m.to.diff(reportRequest.to!).milliseconds === 0,
    );
  }

  return (
    <div className="container-v2 p-2">
      <div className="flex justify-between items-center">
        <h1 className="my-2">Cash Basis Tax</h1>
      </div>
      {reportRequest && (
        <div className="tax-report flex flex-column gap-4">
          <div className="flex flex-wrap justify-between items-end gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <select
                className="h-fit text-[14px]"
                value={selectedMonth}
                required={false}
                onChange={(e) => {
                  const range = reportingMonths[parseInt(e.target.value)];
                  setReportRequest({
                    from: range.from,
                    to: range.to,
                  });
                }}
              >
                {selectedMonth < 0 && <option value={-1}></option>}
                {reportingMonths.map((m, i) => (
                  <option key={i} value={i}>
                    {m.from.toFormat('LLLL yyyy')}
                  </option>
                ))}
              </select>
              <div className="">
                <label className="font-medium">From</label>
                <DatePicker
                  value={reportRequest.from}
                  onChange={(from) =>
                    from && setReportRequest((prevState) => ({ ...prevState!, from: from.startOf('day') }))
                  }
                />
              </div>
              <div className="">
                <label className="font-medium">To</label>
                <DatePicker
                  value={reportRequest.to}
                  onChange={(to) => to && setReportRequest((prevState) => ({ ...prevState!, to: to.endOf('day') }))}
                />
              </div>
              {/*<div className="flex flex-column">*/}
              {/*  <label className="font-medium">Basis</label>*/}
              {/*  <select*/}
              {/*    className="text-[14px]"*/}
              {/*    value={basis}*/}
              {/*    onChange={(e) => {*/}
              {/*      const basis = e.target.value;*/}
              {/*      if (basis === 'cash' || basis === 'accrual') {*/}
              {/*        setBasis(basis);*/}
              {/*      }*/}
              {/*    }}*/}
              {/*  >*/}
              {/*    <option value="cash">Cash</option>*/}
              {/*    <option value="accrual">Accural</option>*/}
              {/*  </select>*/}
              {/*</div>*/}
            </div>
            <Button className="button-yellow font-semibold h-fit" onClick={exportReport} data-cy="export">
              Export
              <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
            </Button>
          </div>
          {basis === 'cash' && <CashBasisTaxReport reportRequest={reportRequest} currency={currency} />}
          <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Tax Report'} />
        </div>
      )}
    </div>
  );
};
